// ============================================================
// dev-core-web/src/App.tsx
// Version: 1.2.0
// Purpose:
//   - root React-компонент UI: маршрутизация + auth-guard
//   - реальные страницы из src/pages/** подключены через
//     React.lazy (code-splitting, только default export)
//   - общий shell — components/Layout (data-testid="app-shell")
//   - работает только через HTTP API dev-core
//   - НЕ импортирует Core напрямую
//   - respects RBAC + Classification (на server-side)
//
// Правила:
//   - только HTTP API (API.md §1)
//   - никаких секретов в UI (SECURITY.md §9.3)
//   - client-side RBAC — только для UX, не security
//   - server-side всё равно проверит RBAC/Classification/Approval
//   - публичные роуты: / (лендинг) и /login; остальные — под RequireAuth
//   - SPA-переходы только через react-router (Link/NavLink),
//     никаких <a href> на навигационных пунктах
//
// Изменения 1.2.0 (Phase 7B):
//   - placeholder-роуты заменены на lazy-импорты реальных страниц
//   - добавлен RequireAuth (useAuth + useAuthBootstrap)
//   - общий shell — components/Layout вместо локального AppShell
//   - локальные placeholder-компоненты удалены; заглушки
//     «coming soon» остались только для роутов без страниц
// ============================================================

import {
  Suspense,
  lazy,
  useEffect,
  type ReactElement,
} from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import Layout from '@/components/Layout';
import { ToastStack } from '@/components/ToastStack';
import { useAuth, useAuthBootstrap } from '@/hooks/useAuth';
import { handleQueryError, subscribeUnauthorized } from '@/lib/queryClient';

// ------------------------------------------------------------
// QueryClient
// ------------------------------------------------------------
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        // Не ретраить 4xx
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
      // 401 из mutation → сброс auth (контракт setUnauthorizedHandler).
      onError: handleQueryError,
    },
  },
});

// 401 из query → сброс auth. Без подписки глобальные хуки
// setUnauthorizedHandler/ useCan / permissionsStore не работают.
subscribeUnauthorized(queryClient);

// ------------------------------------------------------------
// Lazy pages (src/pages/**, только default export)
// ------------------------------------------------------------

const Landing = lazy(() => import('@/pages/Landing'));
const Login = lazy(() => import('@/pages/Login'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Fleet = lazy(() => import('@/pages/Fleet'));
const Incidents = lazy(() => import('@/pages/Incidents'));
const IncidentDetail = lazy(() => import('@/pages/IncidentDetail'));
const Users = lazy(() => import('@/pages/Users'));
const UserDetail = lazy(() => import('@/pages/UserDetail'));
const Roles = lazy(() => import('@/pages/Roles'));
const RoleDetail = lazy(() => import('@/pages/RoleDetail'));
const Permissions = lazy(() => import('@/pages/Permissions'));
const AuditLog = lazy(() => import('@/pages/AuditLog'));
const AccessRequests = lazy(() => import('@/pages/AccessRequests'));
const Clearances = lazy(() => import('@/pages/Clearances'));
const SecuritySettings = lazy(() => import('@/pages/SecuritySettings'));
const Inventory = lazy(() => import('@/pages/Inventory'));
const EntityDetail = lazy(() => import('@/pages/EntityDetail'));
const Tenants = lazy(() => import('@/pages/Tenants'));
const TenantDetail = lazy(() => import('@/pages/TenantDetail'));
const Metrics = lazy(() => import('@/pages/Metrics'));
const LogsViewer = lazy(() => import('@/pages/LogsViewer'));
const Processes = lazy(() => import('@/pages/Processes'));
const Containers = lazy(() => import('@/pages/Containers'));
const Ports = lazy(() => import('@/pages/Ports'));
const Services = lazy(() => import('@/pages/Services'));
const Sites = lazy(() => import('@/pages/Sites'));
const RedisInfoPage = lazy(() => import('@/pages/RedisInfo'));
const PrometheusPage = lazy(() => import('@/pages/PrometheusPage'));
const StoragePage = lazy(() => import('@/pages/Storage'));
const Graph = lazy(() => import('@/pages/Graph'));
const Products = lazy(() => import('@/pages/Products'));
const ProductDetail = lazy(() => import('@/pages/ProductDetail'));
const Revenue = lazy(() => import('@/pages/Revenue'));
const Copilot = lazy(() => import('@/pages/Copilot'));
const XRConsole = lazy(() => import('@/pages/XRConsole'));
const ServerDetail = lazy(() => import('@/pages/ServerDetail'));
const NotFound = lazy(() => import('@/pages/NotFound'));

// ------------------------------------------------------------
// Fallback-компоненты
// ------------------------------------------------------------

function LoadingFallback(): ReactElement {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background text-foreground"
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-3">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary"
          aria-hidden="true"
        />
        <span className="text-sm text-muted-foreground">Loading…</span>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Auth-guard
// ------------------------------------------------------------

//
// RequireAuth: проверяет authStore (useAuth).
//   - idle / loading — сессия ещё не известна → loader
//   - unauthenticated → /login (replace, исходный путь в state)
//   - authenticated / error → children (error-состояние pages
//     снимут сами: 401 → applyUnauthenticated → редирект)
//
// Bootstrap (GET /auth/me) выполняется один раз на уровне App
// через useAuthBootstrap — guard только читает status.
//
function RequireAuth({ children }: { children: ReactElement }): ReactElement {
  const location = useLocation();
  const { status, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
}

// /login: если сессия уже активна — сразу /dashboard (replace).
// Редирект после успешного login не дублируем: Login сам
// делает navigate(returnTo → /dashboard).
function LoginRoute(): ReactElement {
  const { isAuthenticated, isLoading } = useAuth();

  if (isAuthenticated && !isLoading) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Login />;
}

// ------------------------------------------------------------
// Route table (protected — внутри Layout + RequireAuth)
// ------------------------------------------------------------

type AppRoute = {
  path: string;
  element: ReactElement;
};

const protectedRoutes: AppRoute[] = [
  // Overview
  { path: '/dashboard', element: <Dashboard /> },
  { path: '/metrics', element: <Metrics /> },
  { path: '/logs', element: <LogsViewer /> },
  { path: '/processes', element: <Processes /> },
  { path: '/containers', element: <Containers /> },
  { path: '/ports', element: <Ports /> },
  { path: '/services', element: <Services /> },
  { path: '/sites', element: <Sites /> },
  { path: '/redis', element: <RedisInfoPage /> },
  { path: '/prometheus', element: <PrometheusPage /> },
  { path: '/storage', element: <StoragePage /> },

  // Экосистема / инфраструктура
  { path: '/inventory', element: <Inventory /> },
  { path: '/inventory/:id', element: <EntityDetail /> },
  { path: '/graph', element: <Graph /> },
  { path: '/fleet', element: <Fleet /> },
  { path: '/fleet/:id', element: <ServerDetail /> },

  // Инциденты
  { path: '/incidents', element: <Incidents /> },
  { path: '/incidents/:id', element: <IncidentDetail /> },
  // Синглярный префикс — алиас из плана Phase 7B
  { path: '/incident/:id', element: <IncidentDetail /> },

  // Доступ / RBAC
  { path: '/users', element: <Users /> },
  { path: '/users/:id', element: <UserDetail /> },
  { path: '/user/:id', element: <UserDetail /> },
  { path: '/roles', element: <Roles /> },
  { path: '/roles/:id', element: <RoleDetail /> },
  { path: '/role/:id', element: <RoleDetail /> },
  { path: '/permissions', element: <Permissions /> },
  { path: '/clearances', element: <Clearances /> },
  { path: '/security', element: <SecuritySettings /> },
  { path: '/approvals', element: <AccessRequests /> },
  // /access-requests — путь в Layout-навигации и ссылках страниц
  { path: '/access-requests', element: <AccessRequests /> },

  // Аудит
  { path: '/audit', element: <AuditLog /> },

  // Тенанты / продукты / выручка
  { path: '/tenants', element: <Tenants /> },
  { path: '/tenants/:id', element: <TenantDetail /> },
  { path: '/tenant/:id', element: <TenantDetail /> },
  { path: '/products', element: <Products /> },
  { path: '/products/:id', element: <ProductDetail /> },
  { path: '/product/:id', element: <ProductDetail /> },
  { path: '/revenue', element: <Revenue /> },

  // AI / XR
  { path: '/copilot', element: <Copilot /> },
  { path: '/xr', element: <XRConsole /> },
];

// ------------------------------------------------------------
// Routes
// ------------------------------------------------------------

function AppRoutes(): ReactElement {
  return (
    <Routes>
      {/* Public: лендинг (https://core.devorbit.ru/) и вход */}
      <Route
        path="/"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <Landing />
          </Suspense>
        }
      />
      <Route
        path="/login"
        element={
          <Suspense fallback={<LoadingFallback />}>
            <LoginRoute />
          </Suspense>
        }
      />

      {/* Protected: общий shell (Layout) + lazy-страницы */}
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        {protectedRoutes.map((r) => (
          <Route
            key={r.path}
            path={r.path}
            element={
              <Suspense fallback={<LoadingFallback />}>{r.element}</Suspense>
            }
          />
        ))}
      </Route>

      {/* 404: внутри RequireAuth, но без shell (полноэкранный NotFound) */}
      <Route
        path="*"
        element={
          <RequireAuth>
            <Suspense fallback={<LoadingFallback />}>
              <NotFound />
            </Suspense>
          </RequireAuth>
        }
      />
    </Routes>
  );
}

// ------------------------------------------------------------
// App
// ------------------------------------------------------------

export default function App(): ReactElement {
  // Один bootstrap на жизненный цикл приложения: восстановление
  // сессии через GET /auth/me до первого решения RequireAuth
  // (useAuthBootstrap сам защищён от повторных вызовов).
  useAuthBootstrap();

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeBootstrap />
        <AppRoutes />
      </BrowserRouter>
      {/* Стек уведомлений: showToast наполняет uiStore, рендер здесь
          (работает на всех страницах, включая публичные) */}
      <ToastStack />
    </QueryClientProvider>
  );
}

// ------------------------------------------------------------
// Theme bootstrap
// ------------------------------------------------------------
//
// Публичные страницы (лендинг, /login) всегда dark — без выбора
// темы (UI_STYLE §11.1). Сохранённый 'light' применяется только
// внутри приложения (Layout). NotFound форсит dark сам.
const PUBLIC_DARK_PATHS = new Set(['/', '/login']);

function ThemeBootstrap(): null {
  const location = useLocation();

  useEffect(() => {
    if (PUBLIC_DARK_PATHS.has(location.pathname)) {
      document.documentElement.classList.add('dark');
    }
  }, [location.pathname]);

  return null;
}
